/* eslint-disable @typescript-eslint/no-require-imports */
'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import React from 'react'
import { useLoader } from '@/lib/context/LoaderProvider'
import { useGameEntry } from '@/lib/context/GameEntryProvider'
import { usePathname } from 'next/navigation'
import { BrandedLoader } from '@/components/ui/BrandedLoader'
import ConditionalBackground from '@/components/ConditionalBackground'
import ConditionalNavigation from '@/components/ConditionalNavigation'
import { DevPanel } from '@/components/debug/DevPanel'
import { shouldRouteUseNarrowAsMobile, useDevice } from '@/lib/context/DeviceProvider'

// Conditional imports for Vercel Analytics
let Analytics: any = null
let SpeedInsights: any = null

try {
  const analyticsModule = require('@vercel/analytics/react')
  Analytics = analyticsModule.Analytics
} catch (error) {
  // Not available in dev
}

try {
  const speedInsightsModule = require('@vercel/speed-insights/next')
  SpeedInsights = speedInsightsModule.SpeedInsights
} catch (error) {
  // Not available in dev
}

export function AppContent({ children }: { children: React.ReactNode }) {
  const { isLoaderVisible, hideLoader, loaderType } = useLoader()
  const { shouldShowWelcomeLoader, hideWelcomeLoader, isFirstGameEntry } = useGameEntry()
  const { isHydrated, isMobile, viewportWidth } = useDevice()
  const pathname = usePathname()
  const isBareRoute = pathname === '/account/verify'
  // Routes that fold into the mobile shell on a narrow desktop viewport use the
  // full-height MobileLayout, so they must not get the desktop TopHeader offset
  // (which would push the h-dvh layout down and clip its bottom nav).
  const effectiveMobile = isMobile || shouldRouteUseNarrowAsMobile(pathname, viewportWidth)
  const contentShellPadding = isHydrated && effectiveMobile ? 'pt-0' : 'pt-0 lg:pt-[76px]'

  // Determine if any loader should be shown
  const showLoader = isLoaderVisible || shouldShowWelcomeLoader
  const currentLoaderType = shouldShowWelcomeLoader ? 'welcome' : loaderType

  // Get appropriate loader message
  const getLoaderMessage = () => {
    if (shouldShowWelcomeLoader && isFirstGameEntry) {
      return 'Welcome to NPFC!'
    }
    return 'Loading NPFC...'
  }

  // Handle loader completion
  const handleLoaderComplete = () => {
    if (shouldShowWelcomeLoader) {
      hideWelcomeLoader()
    } else {
      hideLoader()
    }
  }

  return (
    <>
      {showLoader && (
        <BrandedLoader
          onComplete={handleLoaderComplete}
          message={getLoaderMessage()}
          className={currentLoaderType === 'welcome' ? 'welcome-loader' : ''}
        />
      )}

      <div
        style={{
          opacity: showLoader ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: showLoader ? 'none' : 'auto'
        }}
      >
        {!isBareRoute && <ConditionalBackground />}
        {!isBareRoute && <ConditionalNavigation />}

        <div className={`relative z-20 min-h-screen ${contentShellPadding}`}>{children}</div>

        {process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === 'true' && (
          <>
            {Analytics && <Analytics />}
            {SpeedInsights && <SpeedInsights />}
          </>
        )}

        <DevPanel />
      </div>
    </>
  )
}
